<script setup>
import { Clock } from 'lucide-vue-next'
import { computed, nextTick, ref, watch } from 'vue'
import {
  CalendarDate,
  CalendarDateTime,
  DateFormatter,
  getLocalTimeZone,
  toZoned,
  today,
} from '@internationalized/date'
import { toDate } from 'reka-ui/date'
import { Calendar } from '@/components/ui/calendar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      // Create a default CalendarDateTime with current time
      const now = new Date()
      const todayDate = today(getLocalTimeZone())
      return new CalendarDateTime(
        todayDate.year,
        todayDate.month,
        todayDate.day,
        now.getHours(),
        Math.floor(now.getMinutes() / 15) * 15, // Round to nearest 15 min
      )
    },
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

// Create date formatter for display
const dateFormatter = new DateFormatter('fr-FR', {
  dateStyle: 'long',
  timeStyle: 'short',
})

// State for selected date
const selectedDate = ref(null)

// We need to track hours and minutes separately since CalendarDate doesn't include time
const selectedHour = ref('12')
const selectedMinute = ref('00')

// Flag to prevent recursive updates
const isUpdating = ref(false)

// Generate hours options (00-23)
const hours = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, '0'))

// Generate minutes options (00, 15, 30, 45)
const minutes = ['00', '15', '30', '45']

// Format the selected date and time for display
const formattedDateTime = computed(() => {
  try {
    // If we have a complete date time value
    if (props.modelValue) {
      // Convert to JavaScript Date for formatting
      return dateFormatter.format(
        toDate(toZoned(props.modelValue, getLocalTimeZone())),
      )
    }
    return ''
  }
  catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
})

// Initialize component state from props
function initializeFromModelValue() {
  if (isUpdating.value)
    return

  try {
    if (props.modelValue) {
      // Set selected date from model value (extract date part only)
      selectedDate.value = new CalendarDate(
        props.modelValue.year,
        props.modelValue.month,
        props.modelValue.day,
      )

      // Set time components
      selectedHour.value = props.modelValue.hour.toString().padStart(2, '0')
      selectedMinute.value = props.modelValue.minute
        .toString()
        .padStart(2, '0')
    }
    else {
      // Default to today
      selectedDate.value = today(getLocalTimeZone())

      // Default time
      const now = new Date()
      selectedHour.value = now.getHours().toString().padStart(2, '0')
      selectedMinute.value = (Math.floor(now.getMinutes() / 15) * 15)
        .toString()
        .padStart(2, '0')
    }
  }
  catch (error) {
    console.error('Error initializing from model value:', error)
    // Reset to defaults on error
    selectedDate.value = today(getLocalTimeZone())
    selectedHour.value = '12'
    selectedMinute.value = '00'
  }
}

// Update the full date/time when components change
function updateDateTime() {
  if (isUpdating.value || !selectedDate.value)
    return

  try {
    isUpdating.value = true

    // Create a new CalendarDateTime object
    const newDateTime = new CalendarDateTime(
      selectedDate.value.year,
      selectedDate.value.month,
      selectedDate.value.day,
      Number.parseInt(selectedHour.value, 10),
      Number.parseInt(selectedMinute.value, 10),
    )

    // Emit the updated value
    emit('update:modelValue', newDateTime)

    // Reset update flag after a small delay
    nextTick(() => {
      isUpdating.value = false
    })
  }
  catch (error) {
    console.error('Error updating date time:', error)
    isUpdating.value = false
  }
}

// Watch for model value changes
watch(
  () => props.modelValue,
  () => {
    if (!isUpdating.value) {
      isUpdating.value = true
      initializeFromModelValue()
      nextTick(() => {
        isUpdating.value = false
      })
    }
  },
  { immediate: true, deep: true },
)

// Watch for date changes
watch(
  selectedDate,
  () => {
    if (!isUpdating.value && selectedDate.value) {
      updateDateTime()
    }
  },
  { deep: true },
)

// Watch for time changes
watch(
  [selectedHour, selectedMinute],
  () => {
    if (!isUpdating.value && selectedDate.value) {
      updateDateTime()
    }
  },
  { deep: true },
)

// Handle date selection
function handleDateSelect(date) {
  if (date) {
    selectedDate.value = date
  }
}

// Initialize component on mount
initializeFromModelValue()
</script>

<template>
  <div class="inline-date-time-picker border rounded-md">
    <div class="p-2 bg-muted/50">
      <p class="text-sm font-medium mb-1">
        {{ formattedDateTime }}
      </p>
    </div>

    <Calendar
      v-model="selectedDate"
      weekday-format="short"
      calendar-label="Date selection"
      mode="single"
      class="p-3 border-b"
      :disabled="disabled"
      @update:model-value="handleDateSelect"
    />

    <div class="p-3 border-t">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <Clock class="h-4 w-4 text-muted-foreground" />
          <span class="text-sm text-muted-foreground">Heure</span>
        </div>
        <div class="flex space-x-2">
          <Select v-model="selectedHour" :disabled="disabled">
            <SelectTrigger class="w-[70px]">
              <SelectValue :placeholder="selectedHour" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="hour in hours" :key="hour" :value="hour">
                {{ hour }}
              </SelectItem>
            </SelectContent>
          </Select>
          <span class="flex items-center text-muted-foreground">:</span>
          <Select v-model="selectedMinute" :disabled="disabled">
            <SelectTrigger class="w-[70px]">
              <SelectValue :placeholder="selectedMinute" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="minute in minutes"
                :key="minute"
                :value="minute"
              >
                {{ minute }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  </div>
</template>
